import { useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import type { SharedRecord } from '../../api/sharedRecordReviewClient';

const REASON_MAX = 400;

interface Props {
  record: SharedRecord;
  busy: boolean;
  error: string | null;
  onApprove: () => void;
  onReject: (reason: string) => void;
}

export function SharedRecordDetail({ record, busy, error, onApprove, onReject }: Props) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const flagged = record.status === 'Flagged';

  const submitReject = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setReasonError('Enter a reason before rejecting.');
      return;
    }
    setReasonError(null);
    onReject(trimmed);
  };

  const btn = 'min-h-[48px] rounded-lg px-4 text-sm font-medium disabled:opacity-50';

  return (
    <section aria-label="Selected record" className="rounded-xl border border-zinc-800 bg-zinc-900 p-4">
      <p className="text-xs text-zinc-500">
        Selected record · confidence {Math.round(record.confidence * 100)}%
      </p>
      <h2 className="mt-0.5 text-base font-semibold text-zinc-100">{record.key}</h2>
      <p className="text-xs text-zinc-500">{record.category}</p>
      <p className="mt-1 text-xs text-zinc-400">
        Source:{' '}
        {record.sourceUrl ? (
          <a
            href={record.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-violet-300 underline hover:text-violet-200"
          >
            {record.sourceUrl}
          </a>
        ) : (
          <span>No source cited</span>
        )}
      </p>

      {flagged && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200"
          role="note"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{record.flagReason ?? 'Flagged: the fresh value contradicts the stored one.'}</span>
        </div>
      )}

      <dl className={`mt-3 grid gap-3 ${flagged ? 'grid-cols-2' : 'grid-cols-1'}`}>
        <div className="rounded-lg bg-zinc-800 p-3">
          <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{flagged ? 'Stored' : 'Value'}</dt>
          <dd className="mt-1 text-sm text-zinc-100">{record.storedValue ?? '—'}</dd>
        </div>
        {flagged && (
          <div className="rounded-lg bg-zinc-800 p-3">
            <dt className="text-[11px] uppercase tracking-wide text-zinc-500">Fresh</dt>
            <dd className="mt-1 text-sm text-zinc-100">{record.freshValue ?? '—'}</dd>
          </div>
        )}
      </dl>

      {error && (
        <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-sm text-red-300">
          {error}
        </p>
      )}

      {rejecting && (
        <div className="mt-3">
          <label htmlFor="reject-reason" className="mb-1 block text-xs text-zinc-400">
            Reason for rejecting (required)
          </label>
          <textarea
            id="reject-reason"
            value={reason}
            maxLength={REASON_MAX}
            rows={3}
            onChange={(e) => setReason(e.target.value)}
            className="w-full resize-y rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:border-violet-500 focus:outline-none"
          />
          <p className="text-[11px] text-zinc-500">
            {reason.length}/{REASON_MAX}
          </p>
          {reasonError && (
            <p role="alert" className="text-xs text-red-400">
              {reasonError}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {!rejecting && (
          <button
            type="button"
            disabled={busy}
            onClick={onApprove}
            className={`${btn} inline-flex items-center gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {flagged ? 'Approve fresh value' : 'Approve'}
          </button>
        )}
        {rejecting ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={submitReject}
              className={`${btn} bg-red-600 text-white hover:bg-red-500`}
            >
              Confirm reject
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setRejecting(false);
                setReasonError(null);
              }}
              className={`${btn} border border-zinc-700 text-zinc-300 hover:border-zinc-600`}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={() => setRejecting(true)}
            className={`${btn} border border-zinc-700 text-zinc-300 hover:border-zinc-600`}
          >
            Reject
          </button>
        )}
      </div>
    </section>
  );
}
